/**
 * Impression tracking with macros — data-layer client (feature #168).
 *
 * Thin typed wrapper over `apiClient` for the impression-tracking routes:
 *
 *   - POST   /api/v1/impression-tracking/templates              — create tpl
 *   - GET    /api/v1/impression-tracking/templates              — list tpls
 *   - GET    /api/v1/impression-tracking/templates/:id          — fetch tpl
 *   - PATCH  /api/v1/impression-tracking/templates/:id          — update tpl
 *   - DELETE /api/v1/impression-tracking/templates/:id          — drop tpl
 *   - POST   /api/v1/impression-tracking/render                 — pure render
 *   - POST   /api/v1/impression-tracking/serve                  — resolve+log
 *   - GET    /api/v1/impression-tracking/impressions            — audit list
 *   - GET    /api/v1/impression-tracking/summary                — facet stats
 *
 * Mirrors server-side view shapes in apps/api/src/routes/impression-tracking.ts.
 * All helpers are pure — dependency-free beyond the apiClient — so they stay
 * trivially testable from the page test file.
 */

import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Shared constants / enums mirrored from the server.
// ---------------------------------------------------------------------------

export const IMPRESSION_SUPPORTED_MACROS = [
	"click_id",
	"campaign_id",
	"affiliate_id",
	"advertiser_id",
	"geo",
	"device",
	"os",
	"event",
	"ts",
	"rnd",
	"template_id",
	"sub1",
	"sub2",
	"sub3",
	"sub4",
	"sub5",
] as const;

export type ImpressionMacroKey = (typeof IMPRESSION_SUPPORTED_MACROS)[number];

export const IMPRESSION_DEVICES = [
	"",
	"desktop",
	"mobile",
	"tablet",
	"other",
] as const;
export const IMPRESSION_OSES = [
	"",
	"ios",
	"android",
	"windows",
	"macos",
	"linux",
	"other",
] as const;

export type ImpressionDevice = (typeof IMPRESSION_DEVICES)[number];
export type ImpressionOs = (typeof IMPRESSION_OSES)[number];

export const IMPRESSION_LABEL_MAX_BYTES = 120;
export const IMPRESSION_URL_TEMPLATE_MAX_BYTES = 2048;
export const IMPRESSION_EVENTS_CSV_MAX_ENTRIES = 32;
export const IMPRESSION_CLICK_ID_MAX = 256;
export const IMPRESSION_SCOPE_ID_MAX = 120;

// ---------------------------------------------------------------------------
// Types — mirrors backend view shape.
// ---------------------------------------------------------------------------

export interface ImpressionTemplate {
	id: string;
	advertiser_id: string;
	campaign_id: string;
	label: string;
	url_template: string;
	events_csv: string;
	enabled: 0 | 1;
	created_at: number;
	updated_at: number;
}

export interface ImpressionRow {
	id: string;
	template_id: string;
	click_id: string;
	campaign_id: string;
	affiliate_id: string;
	advertiser_id: string;
	geo: string;
	device: string;
	os: string;
	event: string;
	rendered_url: string;
	created_at: number;
}

export interface ImpressionSummary {
	from: number | null;
	to: number | null;
	total: number;
	by_geo: Array<{ key: string; count: number }>;
	by_device: Array<{ key: string; count: number }>;
	by_os: Array<{ key: string; count: number }>;
	by_event: Array<{ key: string; count: number }>;
}

export interface ImpressionMacroContext {
	click_id: string;
	campaign_id: string;
	affiliate_id: string;
	advertiser_id: string;
	geo: string;
	device: string;
	os: string;
	event: string;
	ts: number;
	rnd: string;
	template_id: string;
	sub1: string;
	sub2: string;
	sub3: string;
	sub4: string;
	sub5: string;
}

// ---------------------------------------------------------------------------
// Validators — mirror the server-side bounds so the form can pre-empt
// round-trips.
// ---------------------------------------------------------------------------

function byteLength(s: string): number {
	return new TextEncoder().encode(s).length;
}

export function isValidLabel(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const t = v.trim();
	if (!t) return false;
	return byteLength(t) <= IMPRESSION_LABEL_MAX_BYTES;
}

export function isValidUrlTemplate(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const t = v.trim();
	if (!t) return false;
	if (byteLength(t) > IMPRESSION_URL_TEMPLATE_MAX_BYTES) return false;
	// Swap macro tokens for a sentinel so the WHATWG parser accepts the probe.
	const probe = t.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, "X");
	let u: URL;
	try {
		u = new URL(probe);
	} catch {
		return false;
	}
	return u.protocol === "http:" || u.protocol === "https:";
}

export function isValidEventsCsv(v: unknown): boolean {
	if (v === undefined || v === null || v === "") return true;
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (!trimmed || trimmed === "*") return true;
	const parts = trimmed
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
	if (parts.length === 0) return true;
	if (parts.length > IMPRESSION_EVENTS_CSV_MAX_ENTRIES) return false;
	for (const p of parts) {
		if (p.length > 64) return false;
		if (!/^[a-zA-Z0-9_.-]+$/.test(p)) return false;
	}
	return true;
}

export function isValidScopeId(v: unknown): v is string {
	if (v === undefined || v === null || v === "") return true;
	if (typeof v !== "string") return false;
	const t = v.trim();
	return t.length <= IMPRESSION_SCOPE_ID_MAX;
}

export function isValidClickId(v: unknown): v is string {
	if (v === undefined || v === null || v === "") return true;
	if (typeof v !== "string") return false;
	return v.trim().length <= IMPRESSION_CLICK_ID_MAX;
}

export function isValidDevice(v: unknown): v is ImpressionDevice {
	return (
		typeof v === "string" &&
		(IMPRESSION_DEVICES as readonly string[]).includes(v)
	);
}

export function isValidOs(v: unknown): v is ImpressionOs {
	return (
		typeof v === "string" && (IMPRESSION_OSES as readonly string[]).includes(v)
	);
}

// ---------------------------------------------------------------------------
// API bindings
// ---------------------------------------------------------------------------

const BASE = "/impression-tracking";

export interface CreateTemplateArgs {
	label: string;
	url_template: string;
	advertiser_id?: string;
	campaign_id?: string;
	events_csv?: string;
	enabled?: boolean;
}

export async function createImpressionTemplate(
	args: CreateTemplateArgs,
): Promise<ImpressionTemplate> {
	const body: Record<string, unknown> = {
		label: args.label,
		url_template: args.url_template,
	};
	if (args.advertiser_id !== undefined) body.advertiser_id = args.advertiser_id;
	if (args.campaign_id !== undefined) body.campaign_id = args.campaign_id;
	if (args.events_csv !== undefined) body.events_csv = args.events_csv;
	if (args.enabled !== undefined) body.enabled = args.enabled ? 1 : 0;
	return apiClient.post<ImpressionTemplate>(`${BASE}/templates`, body);
}

export async function listImpressionTemplates(filters: {
	advertiser_id?: string;
	campaign_id?: string;
} = {}): Promise<{ templates: ImpressionTemplate[] }> {
	const qs = new URLSearchParams();
	if (filters.advertiser_id) qs.set("advertiser_id", filters.advertiser_id);
	if (filters.campaign_id) qs.set("campaign_id", filters.campaign_id);
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	return apiClient.get<{ templates: ImpressionTemplate[] }>(
		`${BASE}/templates${suffix}`,
	);
}

export async function updateImpressionTemplate(
	id: string,
	patch: Partial<CreateTemplateArgs>,
): Promise<ImpressionTemplate> {
	const body: Record<string, unknown> = {};
	if (patch.label !== undefined) body.label = patch.label;
	if (patch.url_template !== undefined) body.url_template = patch.url_template;
	if (patch.advertiser_id !== undefined) {
		body.advertiser_id = patch.advertiser_id;
	}
	if (patch.campaign_id !== undefined) body.campaign_id = patch.campaign_id;
	if (patch.events_csv !== undefined) body.events_csv = patch.events_csv;
	if (patch.enabled !== undefined) body.enabled = patch.enabled ? 1 : 0;
	return apiClient.patch<ImpressionTemplate>(
		`${BASE}/templates/${encodeURIComponent(id)}`,
		body,
	);
}

export async function deleteImpressionTemplate(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/templates/${encodeURIComponent(id)}`,
	);
}

export interface RenderArgs {
	url_template: string;
	context: Partial<ImpressionMacroContext>;
}

export async function renderImpressionPreview(
	args: RenderArgs,
): Promise<{ rendered_url: string; context: ImpressionMacroContext }> {
	return apiClient.post<{
		rendered_url: string;
		context: ImpressionMacroContext;
	}>(`${BASE}/render`, args);
}

export interface ServeArgs {
	click_id?: string;
	campaign_id?: string;
	affiliate_id?: string;
	advertiser_id?: string;
	geo?: string;
	device?: string;
	os?: string;
	event?: string;
	template_id?: string;
	sub1?: string;
	sub2?: string;
	sub3?: string;
	sub4?: string;
	sub5?: string;
}

export interface ServeResponse {
	id: string;
	template_id: string;
	rendered_url: string;
	context: ImpressionMacroContext;
	impression: ImpressionRow;
}

export async function serveImpression(
	args: ServeArgs,
): Promise<ServeResponse> {
	return apiClient.post<ServeResponse>(`${BASE}/serve`, args);
}

export interface ImpressionFilters {
	from?: number;
	to?: number;
	template_id?: string;
	campaign_id?: string;
	affiliate_id?: string;
	click_id?: string;
}

function filtersToQuery(f: ImpressionFilters): string {
	const qs = new URLSearchParams();
	if (f.from !== undefined) qs.set("from", String(f.from));
	if (f.to !== undefined) qs.set("to", String(f.to));
	if (f.template_id) qs.set("template_id", f.template_id);
	if (f.campaign_id) qs.set("campaign_id", f.campaign_id);
	if (f.affiliate_id) qs.set("affiliate_id", f.affiliate_id);
	if (f.click_id) qs.set("click_id", f.click_id);
	const s = qs.toString();
	return s ? `?${s}` : "";
}

export async function listImpressions(
	filters: ImpressionFilters = {},
): Promise<{ impressions: ImpressionRow[]; count: number; limit: number }> {
	return apiClient.get<{
		impressions: ImpressionRow[];
		count: number;
		limit: number;
	}>(`${BASE}/impressions${filtersToQuery(filters)}`);
}

export async function summariseImpressions(
	filters: ImpressionFilters = {},
): Promise<ImpressionSummary> {
	return apiClient.get<ImpressionSummary>(
		`${BASE}/summary${filtersToQuery(filters)}`,
	);
}

// ---------------------------------------------------------------------------
// Pure helpers — preview rendering + formatters + sort.
// ---------------------------------------------------------------------------

/**
 * Client-side macro substitution used for the live preview pane. Mirrors the
 * server-side `renderMacros` but is dependency-free so the preview keeps
 * updating while the operator types — no round trip required per keystroke.
 */
export function renderMacrosClient(
	template: string,
	ctx: Partial<ImpressionMacroContext>,
): string {
	return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_m, key: string) => {
		if (!(IMPRESSION_SUPPORTED_MACROS as readonly string[]).includes(key)) {
			return "";
		}
		const v = (ctx as Record<string, unknown>)[key];
		if (v === undefined || v === null) return "";
		return encodeURIComponent(String(v));
	});
}

/** Walk the template and return the set of macro keys referenced (unique,
 * sorted by first-appearance order). */
export function extractMacroKeys(template: string): string[] {
	const out: string[] = [];
	const seen = new Set<string>();
	const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
	let m: RegExpExecArray | null;
	// biome-ignore lint/suspicious/noAssignInExpressions: standard regex loop
	while ((m = re.exec(template)) !== null) {
		const key = m[1] ?? "";
		if (!seen.has(key)) {
			seen.add(key);
			out.push(key);
		}
	}
	return out;
}

export function sortTemplates(
	rows: readonly ImpressionTemplate[],
): ImpressionTemplate[] {
	return [...rows].sort((a, b) => {
		if (b.updated_at !== a.updated_at) return b.updated_at - a.updated_at;
		if (a.label < b.label) return -1;
		if (a.label > b.label) return 1;
		return 0;
	});
}

export function sortImpressionsNewestFirst(
	rows: readonly ImpressionRow[],
): ImpressionRow[] {
	return [...rows].sort((a, b) => b.created_at - a.created_at);
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatTimestamp(unixSec: number | null | undefined): string {
	if (
		unixSec === null ||
		unixSec === undefined ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	) {
		return "Never";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatEnabledLabel(v: 0 | 1): string {
	return v === 1 ? "Enabled" : "Disabled";
}

export const impressionTrackingQueryKeys = {
	all: ["impression-tracking"] as const,
	templates: (filters: { advertiser_id?: string; campaign_id?: string } = {}) =>
		[
			"impression-tracking",
			"templates",
			filters.advertiser_id ?? "",
			filters.campaign_id ?? "",
		] as const,
	impressions: (filters: ImpressionFilters = {}) =>
		[
			"impression-tracking",
			"impressions",
			filters.template_id ?? "",
			filters.campaign_id ?? "",
			filters.affiliate_id ?? "",
			filters.click_id ?? "",
			filters.from ?? "",
			filters.to ?? "",
		] as const,
	summary: (filters: ImpressionFilters = {}) =>
		[
			"impression-tracking",
			"summary",
			filters.template_id ?? "",
			filters.campaign_id ?? "",
			filters.affiliate_id ?? "",
			filters.click_id ?? "",
			filters.from ?? "",
			filters.to ?? "",
		] as const,
};
